"use client";

import { useMemo, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Disclaimer } from "@/components/disclaimer";
import { computeCompoundInterest } from "@/lib/calculators/compound-interest";
import { formatCompactCurrency, formatCurrency } from "@/lib/format";

function parseAmount(draft: string): number {
  const n = Number(draft.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-hairline bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-tabular text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

/**
 * Rein hypothetischer Zinseszins-Rechner (backlog.md -> Aktienfinder-Benchmark) - für
 * Akademie/Startseite, aktien-unabhängig, ohne Login. Reine Bildungsillustration mit
 * angenommener konstanter Rendite, siehe lib/calculators/compound-interest.ts für die
 * Berechnung. Lineare (nicht geglättete) Chart-Interpolation, Y-Achse startet bei 0 -
 * gleiche Chart-Konventionen wie der Rest der App (siehe CLAUDE.md).
 */
export function CompoundInterestCalculator() {
  const [initialCapital, setInitialCapital] = useState("5000");
  const [monthlyContribution, setMonthlyContribution] = useState("200");
  const [annualReturnPercent, setAnnualReturnPercent] = useState("7");
  const [years, setYears] = useState(25);

  const result = useMemo(
    () =>
      computeCompoundInterest({
        initialCapital: parseAmount(initialCapital),
        monthlyContribution: parseAmount(monthlyContribution),
        annualReturnRate: parseAmount(annualReturnPercent) / 100,
        years,
      }),
    [initialCapital, monthlyContribution, annualReturnPercent, years],
  );

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="font-serif text-lg font-semibold text-ink">Zinseszins-Rechner</span>
        <p className="mt-1 text-xs text-ink-secondary">
          Rein hypothetische Modellrechnung mit einer angenommenen, konstanten jährlichen
          Rendite - keine Prognose für eine konkrete Aktie oder einen konkreten ETF.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Startkapital (€)
          <Input
            type="number"
            min={0}
            value={initialCapital}
            onChange={(e) => setInitialCapital(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Sparrate/Monat (€)
          <Input
            type="number"
            min={0}
            value={monthlyContribution}
            onChange={(e) => setMonthlyContribution(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Angenommene Rendite (%/Jahr)
          <Input
            type="number"
            value={annualReturnPercent}
            onChange={(e) => setAnnualReturnPercent(e.target.value)}
            className="bg-card"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Anlagedauer (Jahre)
          <select
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            className="rounded-md border border-hairline bg-card px-2 py-1.5 text-sm text-ink"
          >
            {[1, 2, 3, 5, 10, 15, 20, 25, 30, 35, 40, 50].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatTile label="Endkapital" value={formatCurrency(result.finalBalance, "EUR")} />
            <StatTile label="Eingezahlt" value={formatCurrency(result.totalContributions, "EUR")} />
            <StatTile label="Zinsertrag" value={formatCurrency(result.totalInterest, "EUR")} />
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="compoundInterestBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C6A15B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C6A15B" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="compoundInterestContributions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16265C" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#16265C" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
                <XAxis
                  dataKey="year"
                  tickFormatter={(y) => `${y}J`}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, "auto"]}
                  tickFormatter={(v) => formatCompactCurrency(v, "EUR")}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  formatter={(value, name) => [
                    formatCurrency(Number(value), "EUR"),
                    name === "balance" ? "Endkapital" : "Eingezahlt",
                  ]}
                  labelFormatter={(y) => `Jahr ${y}`}
                />
                <Area
                  type="linear"
                  dataKey="balance"
                  stroke="#C6A15B"
                  strokeWidth={2}
                  fill="url(#compoundInterestBalance)"
                />
                <Area
                  type="linear"
                  dataKey="totalContributions"
                  stroke="#16265C"
                  strokeWidth={1.5}
                  fill="url(#compoundInterestContributions)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[0.65rem] text-muted-foreground">
            Gold = Endkapital (inkl. Zinseszins), Navy = reine Einzahlungssumme ohne Rendite.
          </p>
        </>
      )}

      <Disclaimer />
    </div>
  );
}
